import React, { createContext, useEffect, useState } from 'react';
import {
  getAssetData,
  getDataModelForService,
  getGlobalEntityAssetByGlobalEntityName,
  loadDataModelForService,
  saveGlobalEntityToAssetRepository,
  updateAssetData,
} from '../store/dataModel/dataModelActions';
import { useDispatch } from 'react-redux';
import { showToastNotification } from '../store/ui/toasts/toastsActions';
import { ToastType } from '../store/ui/toasts/toastsTypes';
import _ from 'lodash';
import { DataModel, getEntitiesFromDataModel } from '@blazemeter/blaze-data-model';
import { Entity, MeModel } from '@blazemeter/blaze-data-model/src/model/DataModel';
import { ManipulatorDataModel } from '@blazemeter/blaze-data-designer-client';
import blazeDataClient from '../blazeData/blazeDataClient';
import store from 'store/configureStore';
import { ApplicationState } from 'store/rootReducer';
import { getErrorMessage } from '../utils/miscUtil';
import { generateGlobalEntityName } from '../blazeData/blazeDataUtils';
import { useIsMount } from '../hooks/useIsMountHook';

export const ProcessingActionContext = createContext(null);

const ProcessingActionsProvider = ({ children, serviceId }) => {
  const [dataModel, setDataModel] = useState<null | MeModel>(null);
  const [dataManipulatorModel, setDataManipulatorModel] = useState<null | ManipulatorDataModel>(null);
  // Current Entity in the modal, we can throw away changes if modal is closed
  // TODO - move this to modal state, does not have to be in context
  const [currentEntity, setCurrentEntity] = useState<null | string>(null);
  const [originalDm, setOriginalDm] = useState<null | MeModel>(null);
  // Global Scope
  const [globalScope, setGlobalScope] = useState(null);
  const [globalScopeAssetId, setGlobalScopeAssetId] = useState(null);
  const dispatch = useDispatch();

  const isMount = useIsMount();

  useEffect(() => {
    if (!isMount) {
      // Clear the state if there is a change of the service in the transaction.
      // Next time user opens state update modal, everything is fetched again.
      setDataModel(null);
      setOriginalDm(null);
      setDataManipulatorModel(null);
      setGlobalScope(null);
      setGlobalScopeAssetId(null);
    }
  }, [serviceId]);

  /**
   * Fetch global scope for current service.
   */
  const fetchGlobalScope = async (dataModelName: string) => {
    try {
      const globalEntityAsset: any = await getGlobalEntityAssetByGlobalEntityName(dataModelName);
      if (_.isNil(globalEntityAsset)) {
        //Prepare empty global scope
        setGlobalScope({});
      } else {
        const data: any = await getAssetData(globalEntityAsset.id);
        setGlobalScope(data);
        setGlobalScopeAssetId(globalEntityAsset.id);
      }
    } catch (e) {
      dispatch(
        showToastNotification(ToastType.Danger, 'Could not load global entity from Blaze Data.', getErrorMessage(e)),
      );
    }
  };

  /**
   * Synchronizes global scope entity with backend. Once done, once done stores it also to local state of the context.
   * @param globalScopeEntity
   */
  const saveGlobalScope = async (globalScopeEntity: any) => {
    try {
      // Update existing
      if (globalScopeAssetId !== null) {
        await updateAssetData(globalScopeAssetId, globalScopeEntity);
      }
      // Create new Global Entity
      else {
        if (_.isNil(dataModel)) {
          throw Error('No Data Model defined yet.');
        }
        const savedAsset: any = await saveGlobalEntityToAssetRepository(serviceId, globalScopeEntity);
        setGlobalScopeAssetId(savedAsset.id);
      }
    } catch (e) {
      dispatch(showToastNotification(ToastType.Danger, 'Could not save global entity', getErrorMessage(e)));
    } finally {
      //Reload data panel, otherwise we cannot see changes
      const appState = store.getState() as ApplicationState;
      if (appState.ui.isDDPanelOpened === true && appState.ui.pageFilter.serviceId !== 0) {
        dispatch(loadDataModelForService(serviceId, true));
      }
    }
  };

  /**
   * Loads models from TDM backend.
   */
  const fetchDataModels = async () => {
    if (_.isNil(serviceId)) {
      const msg = 'Cannot load data model, missing service Id.';
      dispatch(showToastNotification(ToastType.Danger, 'Cannot load data model', msg));
      throw Error(msg);
    }

    let dm: null | MeModel | DataModel | undefined = _.cloneDeep(originalDm);
    const selectedService = (store.getState() as ApplicationState).ui.pageFilter.serviceId;

    // Get value from redux if available
    if (serviceId === selectedService) {
      dm = (store.getState() as ApplicationState).dataModel.dataModel;
    } else {
      dm = originalDm === null ? await getDataModelForService(serviceId) : dm;
    }
    // initial id (not linked yet) has the format 'datamodel/Sm-serviceName-servId'
    // when already linked id looks like: 3b82f792-69ce-4b51-b382-0c44fb0d503
    // so we use this logic to verify that
    if (_.isNil(dm) || _.includes(dm.id!.toLowerCase(), dm.title!.toLowerCase())) {
      await dispatch(loadDataModelForService(serviceId, true, true));
      dm = (store.getState() as ApplicationState).dataModel.dataModel;
    }

    //Start fetching global scope when we do have data model
    const globalEntityName = generateGlobalEntityName(serviceId);
    await fetchGlobalScope(globalEntityName);

    setOriginalDm(_.cloneDeep(dm as MeModel));
    const manipulatorModel = await blazeDataClient.manipulator.load(_.cloneDeep(dm as MeModel));

    //Save manipulator model and plain model
    await updateModel(manipulatorModel);
  };

  const updateModel = async (dmm: ManipulatorDataModel) => {
    const dm = await blazeDataClient.manipulator.extractDataModel(dmm);
    setDataManipulatorModel(dmm);
    setDataModel(_.cloneDeep(dm as MeModel));
  };

  /**
   * Returns list of entities from the model.
   */
  const getEntities = (): {
    [entityName: string]: Entity;
  } | null => {
    if (_.isNil(dataModel)) {
      return null;
    }
    const dmEntities: {
      [entityName: string]: Entity;
    } = getEntitiesFromDataModel(dataModel);
    return dmEntities;
  };

  return (
    <ProcessingActionContext.Provider
      value={
        {
          dataModel,
          dataManipulatorModel,
          originalDm,
          setOriginalDm,
          updateModel,
          fetchDataModels,
          getEntities,
          currentEntity,
          setCurrentEntity,
          serviceId,
          globalScope,
          saveGlobalScope,
        } as any
      }
    >
      {children}
    </ProcessingActionContext.Provider>
  );
};

// eslint-disable-next-line react/display-name
const withProcessingAction = (Child: any) => (props: any) => (
  <ProcessingActionContext.Consumer>{context => <Child {...props} {...context} />}</ProcessingActionContext.Consumer>
);

export { ProcessingActionsProvider, withProcessingAction };
